import { useMemo } from 'react';
import { AutoFeedbackStatusState } from 'react-scale/components/scalers/tasks/ThreadedInteraction/AutoFeedback/components/AutoFeedbackStatus';
import { AutoCritiqueHook } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useAutoCritique';
import { UseAutoFeedbackResponse } from 'react-scale/components/scalers/tasks/ThreadedInteraction/AutoFeedback/hooks/useAutoFeedback';
import { IChatTaskFeatureConfig } from '@scale/shared/types/config';
import {
  ModelResponseEditorStepResponse,
  IndexedResponse,
  IndexedInteractionStepResponse,
  CandidateMessageWithMetadata,
} from '@scale/shared/types/chat/response';
import { AttachmentMetadataType, ChatAttachment } from '@scale/shared/types/chat';
import { MessageMetadata } from '@scale/shared/types/chat-completion';

export function useModelResponseEditorStepValidation({
  autoCritiqueHook,
  autoFeedbackHook,
  autoFeedbackStatus,
  text,
  autoCritiqueConfig,
  enforceAutoCritique,
  hasRanAutoCritique,
  blockSevereCritique,
  shouldUseAutoFeedback,
}: {
  autoCritiqueHook: AutoCritiqueHook;
  autoFeedbackHook: UseAutoFeedbackResponse | null;
  autoFeedbackStatus: AutoFeedbackStatusState;
  text: string;
  autoCritiqueConfig: IChatTaskFeatureConfig['autoCritique'];
  enforceAutoCritique: boolean;
  hasRanAutoCritique: boolean;
  blockSevereCritique: boolean;
  shouldUseAutoFeedback: boolean;
}) {
  const toolTipText = useMemo(() => {
    if (autoCritiqueHook.critiqueData.loading) {
      return 'Critique loading';
    }

    if (enforceAutoCritique && !hasRanAutoCritique) {
      return 'Please get a critique before submitting';
    }

    if (
      blockSevereCritique &&
      autoCritiqueHook.getBlockOnCritiqueSeverity(
        autoCritiqueHook.critiqueData.critique,
        !!autoCritiqueConfig?.enableSeverityBlocking?.blockCriticalSeverity,
        autoCritiqueConfig?.enableSeverityBlocking?.regexToBlock ?? '',
      )
    ) {
      return 'Please modify your response to improve the critique severity';
    }

    const autoFeedbackTooltipText = autoFeedbackHook?.isFetchingFeedback
      ? 'Chat Companion loading'
      : !autoFeedbackHook?.feedback && autoFeedbackStatus !== AutoFeedbackStatusState.ERROR
      ? 'Please run the Chat Companion before submitting'
      : '';

    if (shouldUseAutoFeedback) {
      return autoFeedbackTooltipText;
    }

    if (text === '') {
      return 'Please enter a non-empty response before submitting';
    }

    return '';
  }, [
    autoCritiqueHook,
    enforceAutoCritique,
    hasRanAutoCritique,
    blockSevereCritique,
    autoCritiqueConfig,
    autoFeedbackHook,
    autoFeedbackStatus,
    shouldUseAutoFeedback,
    text,
  ]);

  return { toolTipText };
}

const processChatAttachments = (attachment: ChatAttachment) => {
  const { attachmentType } = attachment;

  if (attachmentType === AttachmentMetadataType.Email) {
    const { metadata } = attachment;
    let parsedMetadata = metadata;
    if (typeof metadata === 'string') {
      try {
        parsedMetadata = JSON.parse(metadata);
      } catch (e) {
        parsedMetadata = [];
      }
    }

    return {
      ...attachment,
      metadata: parsedMetadata,
    };
  } else {
    return attachment;
  }
};

export function getOriginalResponseAttachments(
  response: IndexedResponse<ModelResponseEditorStepResponse> | undefined,
  lastResponseObject: IndexedInteractionStepResponse | null,
): ChatAttachment[] {
  // Grab original response attachments to thread through to the context of model response editor response
  let responseAttachments: ChatAttachment[] = [];
  if (response?.context?.attachments) {
    responseAttachments = response.context.attachments;
  } else if (
    lastResponseObject?.context?.candidates &&
    lastResponseObject.context.candidates.length > 0
  ) {
    // if no model response was selected, default to first model response
    const index = lastResponseObject?.context?.selectedIndex ?? 0;
    if (index < 0) {
      // TODO: Revisit case where selectedIndex is -1
      // https://linear.app/scale-epd/issue/POD-1514/revisiting-selectedindex-=-1
      return [];
    }
    const candidate: CandidateMessageWithMetadata = lastResponseObject.context.candidates[index];
    if (candidate?.attachments) {
      responseAttachments = candidate.attachments.map(processChatAttachments);
    }
  }
  return responseAttachments;
}

export function getOriginalResponseMessageMetadata(
  response: IndexedResponse<ModelResponseEditorStepResponse> | undefined,
  lastResponseObject: IndexedInteractionStepResponse | null,
): MessageMetadata {
  // Grab original response message metadata to thread through to the context of model response editor response
  let responseMessageMetadata: MessageMetadata = {};
  if (response?.context?.messageMetadata) {
    responseMessageMetadata = response.context.messageMetadata;
  } else if (
    lastResponseObject?.context?.candidates &&
    lastResponseObject.context.candidates.length > 0
  ) {
    // if no model response was selected, default to first model response
    const index = lastResponseObject.context.selectedIndex ?? 0;

    if (index < 0) {
      // TODO: Revisit case where selectedIndex is -1
      // https://linear.app/scale-epd/issue/POD-1514/revisiting-selectedindex-=-1
      return {};
    }
    const candidate: CandidateMessageWithMetadata = lastResponseObject.context.candidates[index];
    if (candidate?.message?.messageMetadata) {
      responseMessageMetadata = candidate.message.messageMetadata;
    }
  }
  return responseMessageMetadata;
}
